import classNames from 'classnames'
import { FieldErrorsImpl, useFormContext } from 'react-hook-form'
import isUndefined from 'lodash/isUndefined'
import { PropsWithChildren } from 'react'
import TextInputError from './TextInputError'
import { FULL_NAME_TOO_LONG } from '../../constants/schemaValidation'
import ControlledTextInput from './ControlledTextInput'
import { Label } from '../../shared/compass-ui'
import useFormStore from '../../shared/hooks/useFormStore'

function NamesInputTextInputError({
  children,
  message,
}: PropsWithChildren<{ message: string | undefined }>) {
  if (message === FULL_NAME_TOO_LONG || typeof message !== 'string') {
    return null
  }

  return <TextInputError>{children}</TextInputError>
}

type NestedNames = FieldErrorsImpl<{
  names: {
    [x: string]: unknown
  }
}>

export default function NamesInput() {
  const {
    watch,
    formState: { errors, touchedFields },
  } = useFormContext()
  const [formStore] = useFormStore()
  const { isMulticardFlow } = formStore
  const middleInitialValue = watch('names.middleInitial')
  const { names } = errors as NestedNames
  const dependOnLastName =
    names?.lastName?.message === FULL_NAME_TOO_LONG || touchedFields.names?.lastName
  const hideMiddleInitial = isMulticardFlow && isUndefined(middleInitialValue)

  const getMiddleInitial = () => {
    if (hideMiddleInitial) {
      return null
    }

    return (
      <div className="col-span-1">
        <Label>
          M.I.
          <ControlledTextInput
            name="names.middleInitial"
            rules={{
              deps: dependOnLastName ? ['names.lastName', 'names.firstName'] : '',
            }}
            maxLength={1}
            id="middle-initial-input"
            error={typeof names?.middleInitial?.message === 'string'}
            readOnly={isMulticardFlow}
            autoComplete="additional-name"
          />
        </Label>
        <NamesInputTextInputError message={names?.middleInitial?.message}>
          {names?.middleInitial?.message}
        </NamesInputTextInputError>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-4 md:grid-cols-6 gap-6">
      <div
        className={classNames({
          'col-span-3 md:col-span-2': !hideMiddleInitial,
          'col-span-full md:col-span-3': hideMiddleInitial,
        })}
      >
        <Label htmlFor="first-name-input">Legal First Name</Label>
        <ControlledTextInput
          name="names.firstName"
          rules={{
            deps: dependOnLastName ? ['names.lastName', 'names.middleInitial'] : '',
          }}
          id="first-name-input"
          maxLength={26}
          error={typeof names?.firstName?.message === 'string'}
          readOnly={isMulticardFlow}
          autoComplete="given-name"
        />
        <NamesInputTextInputError message={names?.firstName?.message}>
          {names?.firstName?.message}
        </NamesInputTextInputError>
      </div>

      {getMiddleInitial()}

      <div className="col-span-full md:col-span-3">
        <Label htmlFor="last-name-input">Legal Last Name</Label>
        <ControlledTextInput
          name="names.lastName"
          rules={{
            deps: ['names.firstName', 'names.middleInitial'],
          }}
          maxLength={26}
          id="last-name-input"
          error={typeof names?.lastName?.message === 'string'}
          readOnly={isMulticardFlow}
          autoComplete="family-name"
        />
        {typeof names?.lastName?.message === 'string' && (
          <TextInputError>{names?.lastName?.message}</TextInputError>
        )}
      </div>
    </div>
  )
}
